<template>
  <div class="flex flex-col h-full gap-5">
    <!-- Page Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-slate-800">审核中心</h1>
        <p class="text-sm text-slate-500 mt-1">AI驱动的科研项目全生命周期智能审核，覆盖5大阶段19项审核任务</p>
      </div>
      <div class="flex items-center gap-3">
        <button class="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium transition-colors flex items-center gap-2">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
          </svg>
          导出审核报告
        </button>
        <button class="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium transition-colors flex items-center gap-2">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
          </svg>
          批量审核
        </button>
      </div>
    </div>

    <!-- Stage Overview Cards -->
    <div class="grid grid-cols-5 gap-4">
      <div 
        v-for="(stage, index) in stages" 
        :key="stage.id"
        class="stage-card rounded-xl p-4 cursor-pointer transition-all duration-300"
        :class="[
          selectedStage === index 
            ? 'bg-gradient-to-br from-blue-500 to-cyan-400 text-white shadow-lg shadow-blue-500/25' 
            : 'bg-white hover:shadow-md border border-slate-100'
        ]"
        @click="selectStage(index)"
      >
        <div class="flex items-center justify-between mb-2">
          <div 
            class="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold"
            :class="selectedStage === index ? 'bg-white/20' : 'bg-slate-100 text-slate-600'"
          >
            {{ stage.number }}
          </div>
          <span 
            class="text-xs px-2 py-0.5 rounded-full"
            :class="selectedStage === index ? 'bg-white/20' : getStatusClass(stage.status)"
          >
            {{ getStatusText(stage.status) }}
          </span>
        </div>
        <h3 class="font-semibold text-sm mb-1" :class="selectedStage === index ? 'text-white' : 'text-slate-800'">{{ stage.name }}</h3>
        <p class="text-xs opacity-80" :class="selectedStage === index ? 'text-white/80' : 'text-slate-500'">{{ stage.taskCount }}项审核任务</p>
        <div class="mt-2 h-1 bg-slate-100 rounded-full overflow-hidden">
          <div 
            class="h-full rounded-full transition-all duration-500"
            :class="selectedStage === index ? 'bg-white/50' : 'bg-blue-500'"
            :style="{ width: stage.progress + '%' }"
          ></div>
        </div>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="flex-1 grid grid-cols-3 gap-5 min-h-0">
      <!-- Left: Task List -->
      <div class="col-span-2 glass-card rounded-2xl flex flex-col overflow-hidden">
        <!-- Stage Header -->
        <div class="p-5 border-b border-slate-100">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
                <span class="text-lg font-bold text-white">{{ currentStage.number }}</span>
              </div>
              <div>
                <h2 class="text-lg font-bold text-slate-800">{{ currentStage.name }}</h2>
                <p class="text-sm text-slate-500">{{ currentStage.description }}</p>
              </div>
            </div>
            <div class="flex items-center gap-3">
              <span class="text-sm text-slate-500">
                完成进度: <span class="font-semibold text-slate-800">{{ currentStage.completedTasks }}/{{ currentStage.taskCount }}</span>
              </span>
              <div class="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  class="h-full bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full transition-all duration-500"
                  :style="{ width: (currentStage.completedTasks / currentStage.taskCount * 100) + '%' }"
                ></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Task List -->
        <div class="flex-1 overflow-y-auto p-4">
          <div class="space-y-3">
            <div
            v-for="(task, index) in currentStage.tasks"
            :key="task.id"
            class="task-item rounded-xl border transition-all duration-300 cursor-pointer"
            :class="[
              selectedTask === index 
                ? 'border-blue-500 bg-blue-50/50 shadow-md' 
                : 'border-slate-100 hover:border-slate-200 hover:shadow-sm bg-white'
            ]"
            @click="handleTaskClick(task, index)"
          >
              <div class="p-4">
                <div class="flex items-start gap-4">
                  <!-- Task Number & Status -->
                  <div class="flex-shrink-0">
                    <div 
                      class="w-10 h-10 rounded-xl flex items-center justify-center"
                      :class="getTaskStatusClass(task.status)"
                    >
                      <span v-if="task.status === 'completed'" class="text-lg">✓</span>
                      <span v-else-if="task.status === 'processing'" class="text-lg animate-spin">⏳</span>
                      <span v-else-if="task.status === 'warning'" class="text-lg">⚠️</span>
                      <span v-else-if="task.status === 'error'" class="text-lg">✕</span>
                      <span v-else class="text-sm font-semibold">{{ index + 1 }}</span>
                    </div>
                  </div>

                  <!-- Task Info -->
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center justify-between mb-1">
                      <h3 class="font-semibold text-slate-800">{{ task.name }}</h3>
                      <div class="flex items-center gap-2">
                        <span 
                          class="text-xs px-2 py-1 rounded-full font-medium"
                          :class="getTaskResultClass(task.result)"
                        >
                          {{ task.resultText }}
                        </span>
                        <button 
                          v-if="task.hasReport"
                          class="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-blue-600 transition-colors"
                          title="查看报告"
                        >
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                    <p class="text-sm text-slate-500 mb-2">{{ task.description }}</p>
                    
                    <!-- AI Analysis Preview -->
                    <div v-if="task.aiResult" class="flex items-center gap-4 text-xs">
                      <span class="text-slate-400">AI分析结果:</span>
                      <span class="font-medium" :class="task.aiResult.score >= 90 ? 'text-emerald-600' : task.aiResult.score >= 70 ? 'text-amber-600' : 'text-red-600'">
                        {{ task.aiResult.score }}% {{ task.aiResult.label }}
                      </span>
                      <span class="text-slate-400">置信度: {{ task.aiResult.confidence }}%</span>
                      <span class="text-slate-400">耗时: {{ task.aiResult.time }}s</span>
                    </div>
                  </div>

                  <!-- Action Buttons -->
                  <div class="flex-shrink-0 flex items-center gap-2">
                    <button 
                      v-if="task.status === 'pending'"
                      class="px-3 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium transition-colors"
                      @click.stop="startAudit(task)"
                    >
                      开始审核
                    </button>
                    <button 
                      v-else-if="task.status === 'completed'"
                      class="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium transition-colors"
                      @click.stop="viewReport(task)"
                    >
                      查看报告
                    </button>
                    <button 
                      v-else-if="task.status === 'processing'"
                      class="px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 text-xs font-medium cursor-not-allowed"
                      disabled
                    >
                      审核中...
                    </button>
                  </div>
                </div>

                <!-- Evidence Chain (if expanded) -->
                <div v-if="selectedTask === index && task.evidence" class="mt-4 pt-4 border-t border-slate-100">
                  <h4 class="text-xs font-semibold text-slate-500 mb-2">证据链</h4>
                  <div class="space-y-2">
                    <div 
                      v-for="(evidence, idx) in task.evidence" 
                      :key="idx"
                      class="flex items-start gap-2 p-2 rounded-lg bg-slate-50 text-xs"
                    >
                      <span class="text-slate-400">{{ idx + 1 }}.</span>
                      <span class="text-slate-600">{{ evidence }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right: AI Assistant Panel -->
      <div class="glass-card rounded-2xl p-5 flex flex-col">
        <div class="flex items-center gap-2 mb-4">
          <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
            </svg>
          </div>
          <h3 class="font-bold text-slate-800">AI审核助手</h3>
          <span class="px-2 py-0.5 bg-emerald-100 text-emerald-600 rounded text-xs">在线</span>
        </div>

        <!-- Quick Actions -->
        <div class="space-y-2 mb-4">
          <button class="w-full p-3 rounded-xl bg-slate-50 hover:bg-slate-100 text-left transition-colors group">
            <div class="flex items-center gap-2 mb-1">
              <svg class="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
              </svg>
              <span class="text-sm font-medium text-slate-700">一键全量审核</span>
            </div>
            <p class="text-xs text-slate-500">自动执行当前阶段所有待审核任务</p>
          </button>
          <button class="w-full p-3 rounded-xl bg-slate-50 hover:bg-slate-100 text-left transition-colors group">
            <div class="flex items-center gap-2 mb-1">
              <svg class="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              <span class="text-sm font-medium text-slate-700">生成综合报告</span>
            </div>
            <p class="text-xs text-slate-500">汇总所有审核结果生成PDF报告</p>
          </button>
        </div>

        <!-- Audit Statistics -->
        <div class="border-t border-slate-100 pt-4">
          <h4 class="text-xs font-semibold text-slate-500 mb-3">本阶段统计</h4>
          <div class="grid grid-cols-2 gap-3">
            <div class="p-3 rounded-xl bg-emerald-50">
              <div class="text-2xl font-bold text-emerald-600">{{ currentStage.stats.passed }}</div>
              <div class="text-xs text-emerald-600/70">审核通过</div>
            </div>
            <div class="p-3 rounded-xl bg-amber-50">
              <div class="text-2xl font-bold text-amber-600">{{ currentStage.stats.warning }}</div>
              <div class="text-xs text-amber-600/70">建议修改</div>
            </div>
            <div class="p-3 rounded-xl bg-red-50">
              <div class="text-2xl font-bold text-red-600">{{ currentStage.stats.error }}</div>
              <div class="text-xs text-red-600/70">发现问题</div>
            </div>
            <div class="p-3 rounded-xl bg-slate-50">
              <div class="text-2xl font-bold text-slate-600">{{ currentStage.stats.pending }}</div>
              <div class="text-xs text-slate-500">待审核</div>
            </div>
          </div>
        </div>

        <!-- Recent Activity -->
        <div class="border-t border-slate-100 pt-4 mt-4 flex-1">
          <h4 class="text-xs font-semibold text-slate-500 mb-3">最近审核记录</h4>
          <div class="space-y-2">
            <div v-for="(log, idx) in recentLogs" :key="idx" class="flex items-center gap-2 text-xs">
              <div 
                class="w-2 h-2 rounded-full"
                :class="log.status === 'success' ? 'bg-emerald-500' : log.status === 'warning' ? 'bg-amber-500' : 'bg-red-500'"
              ></div>
              <span class="text-slate-600 flex-1 truncate">{{ log.taskName }}</span>
              <span class="text-slate-400">{{ log.time }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ReviewCenter',
  data() {
    return {
      selectedStage: 1, // 默认选中申报初审
      selectedTask: null,
      stages: [
        {
          id: 'guide',
          number: 1,
          name: '指南凝练',
          description: '通过AI技术优化项目指南方向，分析前沿技术趋势',
          status: 'completed',
          taskCount: 4,
          completedTasks: 4,
          progress: 100,
          stats: { passed: 3, warning: 1, error: 0, pending: 0 },
          tasks: [
            {
              id: '1-1',
              name: '指南方向优化',
              description: '与领域前沿技术方向对比分析，判断指南方向是否具备先进性、弥补技术空白等',
              status: 'completed',
              result: 'passed',
              resultText: '优化建议已生成',
              hasReport: true,
              aiResult: { score: 85, label: '建议强化', confidence: 92, time: 12.5 },
              evidence: ['对比Nature 2024高熵催化综述，建议增加能源存储方向', '与国家重点研发计划指南契合度85%']
            },
            {
              id: '1-2',
              name: '指南指标优化',
              description: '分析关键研究内容是否设计相关指标，辅助指标优化',
              status: 'completed',
              result: 'passed',
              resultText: '指标设置合理',
              hasReport: true,
              aiResult: { score: 92, label: '指标合理', confidence: 95, time: 8.3 },
              evidence: ['5项考核指标均可量化', '指标值与同类项目对比处于中等偏上水平']
            },
            {
              id: '1-3',
              name: '指南内容查重',
              description: '与已有指南查重对比，研判是否存在已立项的指南，减少重复立项',
              status: 'completed',
              result: 'passed',
              resultText: '未发现重复',
              hasReport: true,
              aiResult: { score: 98, label: '无重复', confidence: 96, time: 15.2 },
              evidence: ['与内部知识库对比重复率8.2%', '与外部科技数据库对比重复率3.1%']
            },
            {
              id: '1-4',
              name: '指标缺失/降低/偏低分析',
              description: '与顶层实施方案、外部科技指标对比，分析指标是否降低/偏低',
              status: 'completed',
              result: 'warning',
              resultText: '建议补充安全指标',
              hasReport: true,
              aiResult: { score: 75, label: '建议修改', confidence: 88, time: 10.1 },
              evidence: ['缺少锂硫电池安全性评估指标', '建议增加循环寿命指标']
            }
          ]
        },
        {
          id: 'declare',
          number: 2,
          name: '申报初审',
          description: 'AI自动审核申报材料完整性、规范性，检测与指南一致性',
          status: 'active',
          taskCount: 7,
          completedTasks: 3,
          progress: 43,
          stats: { passed: 2, warning: 1, error: 0, pending: 4 },
          tasks: [
            {
              id: '2-1',
              name: '与指南一致性分析',
              description: '是否属于指南支持范围分析、研究内容覆盖度分析、与项目目标一致性分析',
              status: 'completed',
              result: 'passed',
              resultText: '完全属于指南范围',
              hasReport: true,
              aiResult: { score: 95, label: '高度契合', confidence: 94, time: 18.5 },
              evidence: ['研究方向与指南契合度95%', '研究内容覆盖率100%', '与项目目标一致性98%']
            },
            {
              id: '2-2',
              name: '申报书内容查重',
              description: '与历史申报书内容查重、与外部论文内容查重',
              status: 'processing',
              result: 'pending',
              resultText: '审核中',
              hasReport: false,
              aiResult: null,
              evidence: []
            },
            {
              id: '2-3',
              name: '科研诚信核查',
              description: '申请人被列入失信行为记录、项目成员被撤稿记录、AIGC检测',
              status: 'completed',
              result: 'passed',
              resultText: '无诚信问题',
              hasReport: true,
              aiResult: { score: 100, label: '无问题', confidence: 99, time: 5.2 },
              evidence: ['未命中失信行为记录', '未命中撤稿记录', 'AIGC检测通过率98%']
            },
            {
              id: '2-4',
              name: '申报书格式审查',
              description: '申报书格式规范性检查',
              status: 'pending',
              result: 'pending',
              resultText: '待审核',
              hasReport: false,
              aiResult: null,
              evidence: []
            },
            {
              id: '2-5',
              name: '经费编制审查',
              description: '经费预算合理性分析',
              status: 'warning',
              result: 'warning',
              resultText: '测试费占比偏高',
              hasReport: true,
              aiResult: { score: 72, label: '建议调整', confidence: 85, time: 11.3 },
              evidence: ['测试费用占比38%，建议细化各测试项目', '设备费预算与市场价格偏差15%']
            },
            {
              id: '2-6',
              name: '科技敏感问题审查',
              description: '伦理与机密审查',
              status: 'pending',
              result: 'pending',
              resultText: '待审核',
              hasReport: false,
              aiResult: null,
              evidence: []
            },
            {
              id: '2-7',
              name: '项目限项审查',
              description: '在研项目数量核查',
              status: 'pending',
              result: 'pending',
              resultText: '待审核',
              hasReport: false,
              aiResult: null,
              evidence: []
            }
          ]
        },
        {
          id: 'review',
          number: 3,
          name: '立项评审',
          description: '智能匹配评审专家，AI辅助内容评审',
          status: 'pending',
          taskCount: 2,
          completedTasks: 0,
          progress: 0,
          stats: { passed: 0, warning: 0, error: 0, pending: 2 },
          tasks: [
            {
              id: '3-1',
              name: 'AI辅助评审',
              description: '生成评审意见初稿，基于历史优秀项目对比、风险提示',
              status: 'pending',
              result: 'pending',
              resultText: '待启动',
              hasReport: false,
              aiResult: null,
              evidence: []
            },
            {
              id: '3-2',
              name: '评审专家遴选',
              description: '智能推荐候选专家，回避原则自动检测、利益冲突识别',
              status: 'pending',
              result: 'pending',
              resultText: '待启动',
              hasReport: false,
              aiResult: null,
              evidence: []
            }
          ]
        },
        {
          id: 'process',
          number: 4,
          name: '过程检查',
          description: '动态监控项目执行进度，AI分析任务书与指南合同一致性',
          status: 'pending',
          taskCount: 3,
          completedTasks: 0,
          progress: 0,
          stats: { passed: 0, warning: 0, error: 0, pending: 3 },
          tasks: [
            {
              id: '4-1',
              name: '任务书与指南/合同分析',
              description: '符合度、偏差度量化分析',
              status: 'pending',
              result: 'pending',
              resultText: '待启动',
              hasReport: false,
              aiResult: null,
              evidence: []
            },
            {
              id: '4-2',
              name: '任务执行情况分析',
              description: '进度偏离、里程碑完成度分析',
              status: 'pending',
              result: 'pending',
              resultText: '待启动',
              hasReport: false,
              aiResult: null,
              evidence: []
            },
            {
              id: '4-3',
              name: '经费执行情况检测',
              description: '执行率、合规性预警',
              status: 'pending',
              result: 'pending',
              resultText: '待启动',
              hasReport: false,
              aiResult: null,
              evidence: []
            }
          ]
        },
        {
          id: 'final',
          number: 5,
          name: '结题审查',
          description: 'AI智能复核验收报告，核查成果真实性',
          status: 'pending',
          taskCount: 3,
          completedTasks: 0,
          progress: 0,
          stats: { passed: 0, warning: 0, error: 0, pending: 3 },
          tasks: [
            {
              id: '5-1',
              name: '项目执行情况审查',
              description: '任务完成度、指标达成度分析',
              status: 'pending',
              result: 'pending',
              resultText: '待启动',
              hasReport: false,
              aiResult: null,
              evidence: []
            },
            {
              id: '5-2',
              name: '经费执行情况审查',
              description: '决算与预算对比分析',
              status: 'pending',
              result: 'pending',
              resultText: '待启动',
              hasReport: false,
              aiResult: null,
              evidence: []
            },
            {
              id: '5-3',
              name: '项目成果审查',
              description: '论文/专利/软著/标准/人才培养真实性核查',
              status: 'pending',
              result: 'pending',
              resultText: '待启动',
              hasReport: false,
              aiResult: null,
              evidence: []
            }
          ]
        }
      ],
      recentLogs: [
        { taskName: '科研诚信核查', status: 'success', time: '2分钟前' },
        { taskName: '指南内容查重', status: 'success', time: '15分钟前' },
        { taskName: '经费编制审查', status: 'warning', time: '1小时前' },
        { taskName: '与指南一致性分析', status: 'success', time: '2小时前' }
      ]
    }
  },
  computed: {
    currentStage() {
      return this.stages[this.selectedStage]
    }
  },
  methods: {
    selectStage(index) {
      this.selectedStage = index
      this.selectedTask = null
    },
    selectTask(index) {
      this.selectedTask = this.selectedTask === index ? null : index
    },
    getStatusClass(status) {
      const classes = {
        completed: 'bg-emerald-100 text-emerald-600',
        active: 'bg-blue-100 text-blue-600',
        pending: 'bg-slate-100 text-slate-500'
      }
      return classes[status] || 'bg-slate-100 text-slate-500'
    },
    getStatusText(status) {
      const texts = {
        completed: '已完成',
        active: '进行中',
        pending: '待启动'
      }
      return texts[status] || '待启动'
    },
    getTaskStatusClass(status) {
      const classes = {
        completed: 'bg-emerald-100 text-emerald-600',
        processing: 'bg-amber-100 text-amber-600',
        warning: 'bg-amber-100 text-amber-600',
        error: 'bg-red-100 text-red-600',
        pending: 'bg-slate-100 text-slate-500'
      }
      return classes[status] || 'bg-slate-100 text-slate-500'
    },
    getTaskResultClass(result) {
      const classes = {
        passed: 'bg-emerald-100 text-emerald-600',
        warning: 'bg-amber-100 text-amber-600',
        error: 'bg-red-100 text-red-600',
        pending: 'bg-slate-100 text-slate-500'
      }
      return classes[result] || 'bg-slate-100 text-slate-500'
    },
    startAudit(task) {
      task.status = 'processing'
      // Simulate audit process
      setTimeout(() => {
        task.status = 'completed'
        task.result = 'passed'
        task.resultText = '审核通过'
        task.hasReport = true
        task.aiResult = { 
          score: Math.floor(Math.random() * 20) + 80, 
          label: '审核通过', 
          confidence: Math.floor(Math.random() * 10) + 85, 
          time: (Math.random() * 10 + 5).toFixed(1) 
        }
        this.currentStage.completedTasks++
        this.currentStage.progress = (this.currentStage.completedTasks / this.currentStage.taskCount * 100)
      }, 2000)
    },
    viewReport(task) {
      // Open report modal or navigate to report page
      alert(`查看报告: ${task.name}`)
    },
    handleTaskClick(task, index) {
      // Handle task click based on task name
      if (task.name === '申报书格式审查') {
        // Navigate to format check page
        this.$router.push('/declare-format-check')
      } else {
        // For other tasks, use the existing behavior
        this.selectTask(index)
      }
    }
  }
}
</script>

<style scoped>
.stage-card {
  transition: all 0.3s ease;
}

.stage-card:hover {
  transform: translateY(-2px);
}

.task-item {
  transition: all 0.3s ease;
}

.task-item:hover {
  transform: translateX(4px);
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
